import React from 'react';
import './RealizedGains.css';

function TaxYearSummary({ yearlyData }) {
  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (!yearlyData || yearlyData.length === 0) {
    return (
      <div className="tax-summary-section">
        <div className="empty-state">
          <p>No closed positions or dividends recorded yet.</p>
          <p className="empty-hint">Start adding transactions to see your yearly summaries here.</p>
        </div>
      </div>
    );
  }

  // Sort years in descending order (most recent first)
  const sortedYears = [...yearlyData].sort((a, b) => b.year - a.year);

  // Calculate "All time" totals across all years
  const allTimeTotals = sortedYears.reduce((acc, { summary }) => {
    const totalRealizedGains = summary.totalRealizedGains || { USD: 0, EUR: 0 };
    const totalDividends = summary.totalDividends || { USD: 0, EUR: 0 };
    const totalWithdrawals = summary.totalWithdrawals || { USD: 0, EUR: 0 };

    acc.realizedGains += totalRealizedGains.EUR || 0;
    acc.dividends += totalDividends.EUR || 0;
    acc.withdrawals += totalWithdrawals.EUR || 0;
    acc.closedPositionsCount += (summary.shortTermGains?.count || 0) + (summary.longTermGains?.count || 0);

    return acc;
  }, { realizedGains: 0, dividends: 0, withdrawals: 0, closedPositionsCount: 0 });

  return (
    <div className="tax-summary-section">
      <h2>Yearly Summary</h2>
      <p className="summary-subtitle">Overview of closed positions, dividends, and withdrawals by year</p>

      {/* All Time Summary */}
      <div className="year-summary-card all-time-card">
        <h3 className="year-heading all-time-heading">📈 All Time</h3>

        <div className="summary-grid compact">
          <div className="summary-card total-gains">
            <div className="card-header">
              <h4>💰 Realized Gains</h4>
            </div>
            <div className="card-body">
              <p className="amount-primary">{formatCurrency(allTimeTotals.realizedGains, 'EUR')}</p>
              <p className="count-info">{allTimeTotals.closedPositionsCount} position{allTimeTotals.closedPositionsCount !== 1 ? 's' : ''}</p>
            </div>
          </div>

          <div className="summary-card dividends">
            <div className="card-header">
              <h4>💵 Dividends</h4>
            </div>
            <div className="card-body">
              <p className="amount-primary">{formatCurrency(allTimeTotals.dividends, 'EUR')}</p>
            </div>
          </div>

          <div className="summary-card withdrawals">
            <div className="card-header">
              <h4>💸 Withdrawals</h4>
            </div>
            <div className="card-body">
              <p className="amount-primary">{formatCurrency(allTimeTotals.withdrawals, 'EUR')}</p>
            </div>
          </div>

          <div className="summary-card total-income">
            <div className="card-header">
              <h4>📊 Total Income</h4>
            </div>
            <div className="card-body">
              <p className="amount-primary">
                {formatCurrency(allTimeTotals.realizedGains + allTimeTotals.dividends, 'EUR')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Individual Year Summaries */}
      {sortedYears.map(({ year, summary }) => {
        const totalRealizedGains = summary.totalRealizedGains || { USD: 0, EUR: 0 };
        const totalDividends = summary.totalDividends || { USD: 0, EUR: 0 };
        const totalWithdrawals = summary.totalWithdrawals || { USD: 0, EUR: 0 };
        const closedPositionsCount = (summary.shortTermGains?.count || 0) + (summary.longTermGains?.count || 0);

        return (
          <div key={year} className="year-summary-card">
            <h3 className="year-heading">{year}</h3>

            <div className="summary-grid compact">
              <div className="summary-card total-gains">
                <div className="card-header">
                  <h4>💰 Realized Gains</h4>
                </div>
                <div className="card-body">
                  <p className="amount-primary">{formatCurrency(totalRealizedGains.EUR, 'EUR')}</p>
                  <p className="count-info">{closedPositionsCount} position{closedPositionsCount !== 1 ? 's' : ''}</p>
                </div>
              </div>

              <div className="summary-card dividends">
                <div className="card-header">
                  <h4>💵 Dividends</h4>
                </div>
                <div className="card-body">
                  <p className="amount-primary">{formatCurrency(totalDividends.EUR, 'EUR')}</p>
                </div>
              </div>

              <div className="summary-card withdrawals">
                <div className="card-header">
                  <h4>💸 Withdrawals</h4>
                </div>
                <div className="card-body">
                  <p className="amount-primary">{formatCurrency(totalWithdrawals.EUR, 'EUR')}</p>
                </div>
              </div>

              <div className="summary-card total-income">
                <div className="card-header">
                  <h4>📊 Total Income</h4>
                </div>
                <div className="card-body">
                  <p className="amount-primary">
                    {formatCurrency((totalRealizedGains.EUR || 0) + (totalDividends.EUR || 0), 'EUR')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default TaxYearSummary;
